import React, { useState, useEffect, useCallback } from 'react';
import "../styles/navbar.css";

const Navbar = () => {
const [isMenuOpen, setIsMenuOpen] = useState(false);
const [isScrolled, setIsScrolled] = useState(false);
const [scrollProgress, setScrollProgress] = useState(0);
const [activeLink, setActiveLink] = useState('/'); // ✅ NEW: Track active link

// ✅ NEW: Get current path and update active link
useEffect(() => {
  const updateActiveLink = () => {
    const currentPath = window.location.pathname;
    setActiveLink(currentPath);
  };

  // Set initial active link
  updateActiveLink();

  // Listen for navigation changes (for SPAs)
  window.addEventListener('popstate', updateActiveLink);
  
  // For manual navigation detection
  const observer = new MutationObserver(() => {
    if (window.location.pathname !== activeLink) {
      updateActiveLink();
    }
  });

  observer.observe(document, { subtree: true, childList: true });

  return () => {
    window.removeEventListener('popstate', updateActiveLink);
    observer.disconnect();
  };
}, [activeLink]);

// Memoized scroll handler
const handleScroll = useCallback(() => {
  const scrolled = window.scrollY > 50;
  setIsScrolled(scrolled);
  
  const progress = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
  setScrollProgress(Math.min(progress, 100));
}, []);

// Handle scroll effects
useEffect(() => {
  window.addEventListener('scroll', handleScroll, { passive: true });
  return () => window.removeEventListener('scroll', handleScroll);
}, [handleScroll]);

// Enhanced menu toggle
const toggleMenu = useCallback(() => {
  setIsMenuOpen(prev => {
    const newState = !prev;
    if (newState) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return newState;
  });
}, []);

// ✅ ENHANCED: Link click handler with active state update
const handleLinkClick = useCallback((e, href) => {
  // Update active link immediately for better UX
  setActiveLink(href);
  
  // Close mobile menu if open
  if (window.innerWidth <= 768 && isMenuOpen) {
    setIsMenuOpen(false);
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  }

  // ✅ NEW: Add small delay to ensure state updates
  setTimeout(() => {
    if (window.location.pathname !== href) {
      setActiveLink(window.location.pathname);
    }
  }, 100);
}, [isMenuOpen]);

// Cleanup on unmount
useEffect(() => {
  return () => {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  };
}, []);

// Handle escape key
useEffect(() => {
  const handleEscape = (e) => {
    if (e.key === 'Escape' && isMenuOpen) {
      setIsMenuOpen(false);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  };

  if (isMenuOpen) {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }
}, [isMenuOpen]);

// ✅ UPDATED: Navigation links without hardcoded active states
const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/services', label: 'Services' },
  { href: '/industries', label: 'Industries' },
  { href: '/contact', label: 'Contact' }
];

// ✅ NEW: Helper function to check if link is active
const isLinkActive = useCallback((href) => {
  if (href === '/' && activeLink === '/') return true;
  if (href !== '/' && activeLink.startsWith(href)) return true;
  return false;
}, [activeLink]);

return (
  <>
    {/* Main Navbar */}
    <nav className={`colee-navbar-container fixed-top ${isScrolled ? 'colee-scrolled' : ''}`}>
      
      {/* Background Effects */}
      <div className="colee-nav-particles" aria-hidden="true">
        {[1,2,3,4,5,6].map(i => (
          <div key={i} className={`colee-particle colee-particle-${i}`}></div>
        ))}
      </div>

      {/* Bootstrap Container */}
      <div className="container-fluid">
        <div className="colee-navbar-content d-flex justify-content-between align-items-center">
          
          {/* Logo Section */}
          <a 
            href="/" 
            className="colee-navbar-logo d-flex align-items-center text-decoration-none"
            onClick={(e) => handleLinkClick(e, '/')}
          >
            <div className="colee-logo-container position-relative">
              <div className="colee-logo-glow" aria-hidden="true"></div>
              <div className="colee-logo-scan-line" aria-hidden="true"></div>
            </div>
            <div className="colee-logo-text ms-3">
              <span className="colee-company-name">COLEE</span>
              <span className="colee-company-tagline">Software Services</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <ul className="colee-nav-links d-none d-md-flex list-unstyled mb-0" role="menubar">
            {navLinks.map((link, index) => (
              <li key={index} className="colee-nav-item" role="none">
                <a 
                  href={link.href}
                  className={`colee-nav-link ${isLinkActive(link.href) ? 'colee-active' : ''}`}
                  onClick={(e) => handleLinkClick(e, link.href)}
                  role="menuitem"
                  tabIndex={0}
                >
                  <span className="colee-nav-link-text">{link.label}</span>
                  <div className="colee-nav-link-glow" aria-hidden="true"></div>
                </a>
              </li>
            ))}
          </ul>

          {/* Mobile Hamburger */}
          <button 
            className={`colee-hamburger-menu d-md-none ${isMenuOpen ? 'colee-active' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
            type="button"
          >
            <div className="colee-hamburger-glow" aria-hidden="true"></div>
            <div className="colee-hamburger-container">
              <span className="colee-hamburger-line"></span>
              <span className="colee-hamburger-line"></span>
              <span className="colee-hamburger-line"></span>
            </div>
          </button>

        </div>
      </div>

      {/* Scroll Progress */}
      <div className="colee-scroll-progress" aria-hidden="true">
        <div 
          className="colee-progress-bar" 
          style={{ width: `${scrollProgress}%` }}
          role="progressbar"
          aria-valuenow={scrollProgress}
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>

    </nav>

    {/* Mobile Navigation Menu */}
    <div 
      className={`colee-mobile-nav ${isMenuOpen ? 'colee-show' : ''}`}
      id="mobile-navigation"
      role="dialog"
      aria-modal="true"
      aria-label="Mobile navigation menu"
    >
      <ul className="colee-mobile-nav-links list-unstyled" role="menu">
        {navLinks.map((link, index) => (
          <li key={index} className="colee-mobile-nav-item" role="none">
            <a 
              href={link.href}
              className={`colee-mobile-nav-link ${isLinkActive(link.href) ? 'colee-active' : ''}`}
              onClick={(e) => handleLinkClick(e, link.href)}
              style={{ '--colee-delay': `${index * 0.1}s` }}
              role="menuitem"
              tabIndex={isMenuOpen ? 0 : -1}
            >
              <span className="colee-nav-link-text">{link.label}</span>
              <div className="colee-nav-link-glow" aria-hidden="true"></div>
            </a>
          </li>
        ))}
        
        {/* Mobile CTA */}
        <li className="colee-mobile-nav-item colee-mobile-cta" role="none">
          <a 
            href="/get-started" 
            className="colee-cta-button"
            onClick={(e) => handleLinkClick(e, '/get-started')}
            role="menuitem"
            tabIndex={isMenuOpen ? 0 : -1}
          >
            Get Started
          </a>
        </li>
      </ul>
    </div>

    {/* Mobile Backdrop */}
    {isMenuOpen && (
      <div 
        className="colee-nav-backdrop"
        onClick={toggleMenu}
        aria-hidden="true"
      ></div>
    )}
  </>
);
};

export default Navbar;